
import jsPDF from "jspdf";
import "jspdf-autotable";

// Helper para obtener nombre del mes
const obtenerNombreMesCustom = (anio, mes) => {
    const meses = [
        "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
        "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
    ];
    const mesIndex = parseInt(mes, 10) - 1;
    return `${meses[mesIndex]} ${anio}`;
};

// Helper para agrupar por mes
export const agruparPorMes = (datos, tipo = "Ingreso", campoFecha = "fecha") => {
    if (!Array.isArray(datos)) return {};

    return datos.reduce((acc, item) => {
        const fechaStr = item[campoFecha];
        if (!fechaStr || typeof fechaStr !== 'string') return acc;

        const [anio, mes] = fechaStr.split("-");
        if (!anio || !mes) return acc;

        const mesClave = `${anio}-${mes}`;
        const mesNombre = obtenerNombreMesCustom(anio, mes);

        if (!acc[mesClave]) {
            acc[mesClave] = {
                descripcion: `${tipo} de ${mesNombre}`,
                cantidad: 0,
                precio_total: 0,
                fecha: mesNombre
            };
        }

        acc[mesClave].cantidad += parseInt(String(item.cantidad || '0').replace(/,/g, ''), 10) || 0;
        acc[mesClave].precio_total += parseFloat(String(item.precio_total || '0').replace(/,/g, '')) || 0;

        return acc;
    }, {});
};

// Helper para agrupar consumos por mes
export const agruparPorMesConsumos = (datos) => {
    if (!Array.isArray(datos)) return [];

    const agrupado = datos.reduce((acc, item) => {
        if (!item.fecha_pago || !item.consumo_total || !item.nombre_cliente) return acc;

        const [anio, mes] = item.fecha_pago.split('-');
        if (!anio || !mes) return acc;

        const mesClave = `${anio}-${mes}`;
        const mesNombre = obtenerNombreMesCustom(anio, mes);

        if (!acc[mesClave]) {
            acc[mesClave] = {
                clientesSet: new Set(),
                consumo_total: 0,
                fecha_pago: mesNombre
            };
        }

        acc[mesClave].clientesSet.add(item.nombre_cliente);
        acc[mesClave].consumo_total += parseFloat(String(item.consumo_total).replace(/,/g, '')) || 0;

        return acc;
    }, {});

    return Object.values(agrupado).map((item) => ({
        nombre_cliente: item.clientesSet.size,
        consumo_total: item.consumo_total,
        fecha_pago: item.fecha_pago
    }));
};

const drawPDFHeader = (doc, { empresaNombre, usuarioNombre, titulo, extraInfo, totalAmount }) => {
    // Nombre Empresa
    doc.setFontSize(22);
    doc.setTextColor(0, 0, 0);
    doc.text(empresaNombre || 'Junta de Agua', doc.internal.pageSize.width / 2, 20, { align: 'center' });
    
    // Título Reporte
    doc.setFontSize(14);
    doc.text(`Reporte: ${titulo}`, doc.internal.pageSize.width / 2, 28, { align: 'center' });
    
    let yPos = 40;
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    
    // Información extra (ej. Fechas)
    if (extraInfo) {
        doc.text(extraInfo, doc.internal.pageSize.width / 2, 34, { align: 'center' });
        yPos += 4;
    }
    
    // Usuario
    doc.text(`Generado por: ${usuarioNombre || 'Usuario'}`, 14, yPos);
    yPos += 6;
    
    // Fecha actual
    const fechaHora = new Date().toLocaleString('es-EC');
    doc.text(`Generado el: ${fechaHora}`, 14, yPos);
    yPos += 10;
    
    // Total (opcional)
    if (totalAmount !== undefined && totalAmount !== null) {
        doc.setFontSize(12);
        // Verde #22c55e (rgb 34, 197, 94)
        doc.setTextColor(34, 197, 94);
        doc.setFont("helvetica", "bold");
        doc.text(`Monto total en deudas: $${Number(totalAmount).toFixed(2)}`, 14, yPos);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(0, 0, 0);
        yPos += 10;
    } else {
        doc.setTextColor(0, 0, 0);
    }
    
    return yPos;
};

export const generatePDF = (selectedReport, reportData, extraData = {}) => {
    const doc = new jsPDF();
    const { fechaInicio, fechaFin, tipoReporte, selectedDate } = extraData;

    try {
        if (selectedReport === "medidores" && reportData) {
            const startY = drawPDFHeader(doc, { 
                ...extraData, 
                titulo: "Listado de Medidores" 
            });

            // Generación de tabla
            doc.autoTable({
                head: [
                    [
                        "#",
                        "Código Medidor",
                        "Ubicación",
                        "Fecha Instalación",
                        "Nombre",
                        "Estado",
                        "Cliente",
                        "Tarifa",
                    ],
                ],
                body: reportData.map((medidor, index) => [
                    index + 1,
                    medidor.codigo_medidor,
                    medidor.ubicacion_medidor,
                    medidor.fecha_instalacion,
                    medidor.nombre_medidor,
                    medidor.estado_medidor,
                    medidor.cliente?.nombre_cliente || 'N/A',
                    medidor.tarifa?.tipo_tarifa || 'N/A',
                ]),
                startY: startY,
            });

            doc.save("reporte_medidores.pdf");
        }
        else if (selectedReport === "clientes" && reportData) {
            const startY = drawPDFHeader(doc, { 
                ...extraData, 
                titulo: "Listado de Clientes" 
            });
            // Generación de tabla
            doc.autoTable({
                head: [
                    [
                        "#",
                        "Nombre",
                        "Cédula del Cliente",
                        "Dirección",
                        "Teléfono",
                        "Email",
                        "Fecha Registro",
                    ],
                ],
                body: reportData.map((cliente, index) => [
                    index + 1,
                    cliente.nombre_cliente,
                    cliente.codigo_cliente,
                    cliente.direccion_cliente,
                    cliente.telefono_cliente,
                    cliente.email_cliente,
                    cliente.fecha_registro,
                ]),
                startY: startY,
                columnStyles: {
                    1: { cellWidth: 40 }, // Ajusta el ancho de la columna "Nombre"
                },
            });

            doc.save("reporte_clientes.pdf");
        }
        else if (selectedReport === "suma-total" && reportData) {
            const totalRecaudado = reportData.total || 0;
            const startY = drawPDFHeader(doc, { 
                ...extraData, 
                titulo: "Consumos Mensuales",
                extraInfo: `Fecha de Cobro: ${selectedDate || 'N/A'}`,
                totalAmount: totalRecaudado
            });

            // Generación de tabla
            doc.autoTable({
                head: [
                    [
                        "#", "Medidor", "Cliente", "L. Ant", "L. Act", "m³",
                        "Base", "Exceso", "Mora", "Reconex", "Sub Agua", "Deudas", "Total"
                    ],
                ],
                body: reportData.pagos.map((pago, index) => [
                    index + 1,
                    pago.codigo_medidor || pago.medidor?.codigo_medidor || 'N/A',
                    pago.nombre_cliente,
                    pago.lectura_anterior,
                    pago.lectura_actual,
                    pago.consumos?.m3 || (pago.lectura_actual - pago.lectura_anterior),
                    `$${Number(pago.valores?.base || 0).toFixed(2)}`,
                    `$${Number(pago.valores?.valorExceso || 0).toFixed(2)}`,
                    `$${Number(pago.valores?.mora || 0).toFixed(2)}`,
                    `$${Number(pago.valores?.reconexion || 0).toFixed(2)}`,
                    `$${Number(pago.valores?.subtotal || 0).toFixed(2)}`,
                    `$${Number(pago.valores?.deuda_extra || 0).toFixed(2)}`,
                    `$${Number(pago.total_pagar || 0).toFixed(2)}`
                ]),
                startY: startY,
                columnStyles: {
                    0: { cellWidth: 8 },
                    2: { cellWidth: 35 },
                },
                styles: { fontSize: 7, cellPadding: 1 },
            });

            // Calcular posición actual
            const totalHeight = doc.lastAutoTable.finalY + 10;

            // Dimensiones de la página
            const pageHeight = doc.internal.pageSize.height;

            // Espacio que necesitas para los textos
            const espacioNecesario = 50;

            // Verificar si hay espacio suficiente
            if (totalHeight + espacioNecesario > pageHeight) {
                doc.addPage();
                // Reiniciar altura para la nueva página
                doc.text(`Fecha de Cobro: ${selectedDate}`, 14, 20);
                doc.text(`Total Consumo m3: ${reportData?.totalConsumo || 0}`, 14, 30);
                doc.text(`Total Exceso m3: ${reportData?.totalExceso || 0}`, 14, 40);
                doc.text(`Total de Dinero Cobrado $: ${reportData?.total || 0}`, 14, 50);
            } else {
                // Hay espacio suficiente, continuar en la misma página
                doc.text(`Fecha de Cobro: ${selectedDate}`, 14, totalHeight + 10);
                doc.text(`Total Consumo m3: ${reportData?.totalConsumo || 0}`, 14, totalHeight + 20);
                doc.text(`Total Exceso m3: ${reportData?.totalExceso || 0}`, 14, totalHeight + 30);
                doc.text(`Total de Dinero Cobrado $: ${reportData?.total || 0}`, 14, totalHeight + 40);
            }

            doc.save("reporte_suma_total.pdf");
        }
        else if (selectedReport === "reporte-rango" && reportData) {
            const egresos = reportData.egresos?.egresos ?? [];
            const ingresos = reportData.ingresos?.ingresos ?? [];
            const deudas = reportData.deudas?.deudas ?? [];
            const consumos = reportData.consumos?.pagos ?? [];

            // Calculo preliminar del total (ingresos + consumos)
            const prelimIngresos = ingresos.reduce((sum, item) => sum + Number(item.precio_total ?? 0), 0);
            const prelimConsumos = consumos.reduce((sum, item) => sum + Number(item.consumo_total ?? 0), 0);
            const totalRecaudadoRango = prelimIngresos + prelimConsumos;

            let startY = drawPDFHeader(doc, { 
                ...extraData, 
                titulo: "Reporte Financiero",
                extraInfo: `Periodo: ${fechaInicio || 'N/A'} al ${fechaFin || 'N/A'}`,
                totalAmount: totalRecaudadoRango
            });

            // Variables para los totales
            let totalIngresos = 0;
            let totalConsumos = 0;
            let totalEgresos = 0;
            let totalDeudas = 0;

            // Siempre agrupar consumos por mes (para día o mes)
            const consumosMensuales = agruparPorMesConsumos(consumos);
            let y = startY + 5;

            if (tipoReporte === "mes") {
                const egresosMensuales = Object.values(agruparPorMes(egresos, "Egreso"));
                const ingresosMensuales = Object.values(agruparPorMes(ingresos, "Ingreso"));
                const deudasMensuales = Object.values(agruparPorMes(deudas, "Deudas por cobrar"));

                // Tabla de Ingresos por mes
                doc.setFontSize(14);
                doc.text("Tabla de Ingresos Generales", doc.internal.pageSize.width / 2, y, { align: "center" });
                y += 5;
                doc.autoTable({
                    head: [["#", "Descripción", "Cantidad", "Precio Total", "Fecha"]],
                    body: ingresosMensuales.map((item, i) => [
                        i + 1,
                        item.descripcion ?? "N/A",
                        Number(item.cantidad ?? 0).toFixed(0),
                        Number(item.precio_total ?? 0).toFixed(2),
                        item.fecha ?? "N/A",
                    ]),
                    startY: y,
                });
                totalIngresos = ingresosMensuales.reduce((sum, item) => sum + Number(item.precio_total ?? 0), 0);
                doc.text(`Total Ingresos: $${totalIngresos.toFixed(2)}`, 14, doc.lastAutoTable.finalY + 5);

                // Tabla de Egresos por mes
                y = doc.lastAutoTable.finalY + 15;
                doc.text("Tabla de Gastos o Egresos", doc.internal.pageSize.width / 2, y, { align: "center" });
                y += 5;
                doc.autoTable({
                    head: [["#", "Descripción", "Cantidad", "Precio Total", "Fecha"]],
                    body: egresosMensuales.map((item, i) => [
                        i + 1,
                        item.descripcion ?? "N/A",
                        Number(item.cantidad ?? 0).toFixed(0),
                        Number(item.precio_total ?? 0).toFixed(2),
                        item.fecha ?? "N/A",
                    ]),
                    startY: y,
                });
                totalEgresos = egresosMensuales.reduce((sum, item) => sum + Number(item.precio_total ?? 0), 0);
                doc.text(`Total Egresos: $${totalEgresos.toFixed(2)}`, 14, doc.lastAutoTable.finalY + 5);

                // Tabla de deudas por mes
                y = doc.lastAutoTable.finalY + 15;
                doc.text("Tabla de deudas por cobrar", doc.internal.pageSize.width / 2, y, { align: "center" });
                y += 5;
                doc.autoTable({
                    head: [["#", "Descripción", "Cantidad", "Precio Total", "Fecha"]],
                    body: deudasMensuales.map((item, i) => [
                        i + 1,
                        item.descripcion ?? "N/A",
                        Number(item.cantidad ?? 0).toFixed(0),
                        Number(item.precio_total ?? 0).toFixed(2),
                        item.fecha ?? "N/A",
                    ]),
                    startY: y,
                });
                totalDeudas = deudasMensuales.reduce((sum, item) => sum + Number(item.precio_total ?? 0), 0);
                doc.text(`Total Deudas por cobrar: $${totalDeudas.toFixed(2)}`, 14, doc.lastAutoTable.finalY + 5);
            } else {
                // Reporte diarios. 
                let y = 40;

                // Tabla de Egresos diarios
                doc.text("Tabla de Gastos o Egresos", doc.internal.pageSize.width / 2, y, { align: "center" });
                y += 5;
                doc.autoTable({
                    head: [["#", "Descripción", "Cantidad", "Precio Total", "Fecha"]],
                    body: egresos.map((item, i) => [
                        i + 1,
                        item.descripcion ?? "N/A",
                        Number(item.cantidad ?? 0).toFixed(0),
                        Number(item.precio_total ?? 0).toFixed(2),
                        item.fecha ?? "N/A",
                    ]),
                    startY: y,
                });
                totalEgresos = egresos.reduce((sum, item) => sum + Number(item.precio_total ?? 0), 0);
                doc.text(`Total Egresos: $${totalEgresos.toFixed(2)}`, 14, doc.lastAutoTable.finalY + 5);

                // Tabla de Ingresos diarios
                y = doc.lastAutoTable.finalY + 15;
                doc.text("Tabla de Ingresos Generales", doc.internal.pageSize.width / 2, y, { align: "center" });
                y += 5;
                doc.autoTable({
                    head: [["#", "Descripción", "Cantidad", "Precio Total", "Fecha"]],
                    body: ingresos.map((item, i) => [
                        i + 1,
                        item.descripcion ?? "N/A",
                        Number(item.cantidad ?? 0).toFixed(0),
                        Number(item.precio_total ?? 0).toFixed(2),
                        item.fecha ?? "N/A",
                    ]),
                    startY: y,
                });
                totalIngresos = ingresos.reduce((sum, item) => sum + Number(item.precio_total ?? 0), 0);
                doc.text(`Total Ingresos: $${totalIngresos.toFixed(2)}`, 14, doc.lastAutoTable.finalY + 5);
                totalDeudas = deudas.reduce((sum, item) => sum + Number(item.precio_total ?? 0), 0);
            }

            // ⚠️ A PARTIR DE AQUÍ: SIEMPRE se muestra la tabla de consumos mensuales
            y = doc.lastAutoTable.finalY + 15;
            doc.text("Tabla de ingresos de consumos", doc.internal.pageSize.width / 2, y, { align: "center" });
            y += 5;
            doc.autoTable({
                head: [["#", "Nro. Planillas", "Monto $", "Fecha De Cobro"]],
                body: consumosMensuales.map((item, i) => [
                    i + 1,
                    item.nombre_cliente ?? "N/A",
                    Number(item.consumo_total ?? 0).toFixed(2),
                    item.fecha_pago ?? "N/A",
                ]),
                startY: y,
            });
            totalConsumos = consumosMensuales.reduce((sum, item) => sum + Number(item.consumo_total ?? 0), 0);
            doc.text(`Total Ingreso de consumos: $${totalConsumos.toFixed(2)}`, 14, doc.lastAutoTable.finalY + 5);

            // Totales generales calculados
            const totalGeneral = totalIngresos + totalConsumos - totalEgresos;
            const totalEfectivo = totalGeneral - totalDeudas;

            let resumenY = doc.lastAutoTable.finalY + 20;
            doc.text(`Cuentas Generales`, doc.internal.pageSize.width / 2, resumenY, { align: "center" });

            let finalY = resumenY + 10;
            const pageHeight = doc.internal.pageSize.height;
            const espacioNecesario = 60;

            if (finalY + espacioNecesario > pageHeight) {
                doc.addPage();
                finalY = 20;
            }
            doc.setFontSize(14);
            doc.text(`Ingresos generales: $${totalIngresos.toFixed(2)}`, 14, finalY);
            doc.text(`Ingreso de Consumos: $${totalConsumos.toFixed(2)}`, 14, finalY + 10);
            doc.text(`Gastos o Egresos: $${totalEgresos.toFixed(2)}`, 14, finalY + 20);
            doc.text(`Deudas por Cobrar: $${totalDeudas.toFixed(2)}`, 14, finalY + 30);

            doc.setFontSize(8);
            doc.text(`(Ingresos generales + Ingreso de Consumos - Egresos)`, 14, finalY + 36);
            doc.setFontSize(14);
            doc.text(`Total General: $${totalGeneral.toFixed(2)}`, 14, finalY + 41);

            doc.setFontSize(8);
            doc.text(`(Total general - Deudas por cobrar)`, 14, finalY + 47);
            doc.setFontSize(14);
            doc.text(`Total Efectivo: $${totalEfectivo.toFixed(2)}`, 14, finalY + 52);

            doc.save("Rendicion de cuentas.pdf");
        }
        else if (selectedReport === "morosos" && reportData) {
            const totalDeudaAcumulada = reportData.reduce((acc, curr) => acc + (curr.totalDeuda || 0), 0);

            const startY = drawPDFHeader(doc, { 
                ...extraData, 
                titulo: "Reporte de Clientes Deudores",
                extraInfo: `Total Clientes deudores: ${reportData.length}`,
                totalAmount: totalDeudaAcumulada
            });

            // Generación de tabla
            doc.autoTable({
                head: [
                    [
                        "#",
                        "Medidor",
                        "Nombre del Cliente",
                        "Meses deuda",
                        "Subtotal Agua",
                        "Deudas Extras",
                        "Total Deuda",
                    ],
                ],
                body: reportData.map((mora, index) => [
                    index + 1,
                    mora.codigoMedidor || 'N/A',
                    mora.cliente,
                    `${mora.mesesMora} meses`,
                    `$${Number(mora.subtotalAgua || 0).toFixed(2)}`,
                    `$${Number(mora.totalDeudasExtra || 0).toFixed(2)}`,
                    `$${Number(mora.totalDeuda || 0).toFixed(2)}`
                ]),
                startY: startY,
                columnStyles: {
                    0: { cellWidth: 8 },
                    3: { halign: 'center' },
                    4: { halign: 'right' },
                    5: { halign: 'right' },
                    6: { halign: 'right', fontStyle: 'bold' }
                }
            });

            doc.save("reporte_morosos.pdf");
        }
    } catch (err) {
        console.error("Error generating PDF:", err);
    }
};
